// Detects when a Vite-built chunk can no longer be loaded after a prod deploy.
//
// Root cause: after deploy, asset filenames change (content-hash). The browser
// has a cached index.html pointing at the old hashes. The old JS files are gone
// from the server, so nginx returns the SPA fallback (index.html, text/html).
// The browser refuses to execute text/html as JavaScript → MIME-type error.
//
// Two detection paths:
//   1. window 'vite:preloadError' — fired by Vite when a dynamic import / preload
//      fails. Hooks in early (before React renders). Shows a DOM-injected banner.
//   2. isStaleChunkError() — called from ErrorBoundary.componentDidCatch when the
//      error propagates up through React.lazy / Suspense instead.
//
// IN-163: a chunk that fails to load mid-session is not always safe to recover
// from with an immediate reload. The lazy HEIC decoder (heic-to/csp) and the
// vendored RAW decoder worker can each be pulled in hours into a multi-thousand-
// photo upload — a network blip on THAT chunk must not reload the page and kill
// the upload queue, which does not survive a reload. `registerReloadBlocker`
// lets a feature (uploadStore) tell this detector "don't reload while I'm busy,
// and tell me when you'd like to reload so I can let you know the moment I'm
// idle" — WITHOUT this file importing the upload store, so main.tsx's early,
// pre-React init stays light regardless of what registers later.

import i18n from '@/i18n'

const STALE_CHUNK_SIGNATURES = [
  'text/html',                                      // MIME mismatch message
  'is not valid JavaScript',                        // Firefox / Safari phrasing
  'Failed to fetch dynamically imported module',    // Chrome
  'error loading dynamically imported module',      // Safari
  'Importing a module script failed',               // Firefox
]

/** Returns true when an error looks like a stale-chunk MIME-type failure. */
export function isStaleChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '')
  return STALE_CHUNK_SIGNATURES.some(sig => msg.includes(sig))
}

/**
 * i18next may not have finished loading the 'common' namespace yet (this can run
 * before React, and the failed chunk itself might be the one carrying
 * translations) — so every call passes an English default and this never
 * suspends or throws. Calling the singleton's `t()` directly (not the
 * `useTranslation()` hook) is what keeps this safe to call from plain DOM code
 * with no component tree above it.
 */
function t(key: string, fallback: string): string {
  try {
    return i18n.t(key, fallback)
  } catch {
    return fallback
  }
}

/**
 * Exported so ErrorBoundary's React-rendered `StaleBuildFallback` can use the
 * exact same "singleton `t()`, English default, never throws" lookup as the
 * DOM-injected banner above — one policy for both halves of stale-build
 * recovery, same as {@link recoverFromStaleBuild} itself.
 */
export const staleBuildText = t

/**
 * A feature that can tell this detector "don't reload while I'm busy, and wake
 * me when you're not" — registered by `uploadStore.ts` so an in-flight upload
 * batch survives a deploy that lands mid-session. See the file header (IN-163).
 */
export interface ReloadBlocker {
  /** True while a reload would destroy in-progress work. */
  isBusy: () => boolean
  /**
   * Calls `onIdle` once each time the blocker transitions from busy to not-busy.
   * Returns an unsubscribe function.
   */
  subscribeIdle: (onIdle: () => void) => () => void
}

let _reloadBlocker: ReloadBlocker | null = null

/** Called once, at module load, by whatever feature needs to defer reloads. */
export function registerReloadBlocker(blocker: ReloadBlocker): void {
  _reloadBlocker = blocker
}

/** Whether recovering right now would destroy work in progress. */
export function isReloadBlocked(): boolean {
  return _reloadBlocker?.isBusy() ?? false
}

/**
 * IN-189: how long after `beforeunload` a failed chunk counts as the page being
 * LEFT rather than a stale deploy. See {@link initStaleBuildDetector}.
 *
 * Long enough to outlast a slow navigation (the old document stays alive, timers
 * and all, until the new response commits); bounded so that a `beforeunload`
 * that never became a navigation — a download link, a `mailto:` — cannot switch
 * recovery off for the rest of the session.
 */
const LEAVING_WINDOW_MS = 10_000

/**
 * IN-189: how long a chunk failure waits before it is judged. WebKit's order is
 * not fixed: `beforeunload` comes BEFORE the cancelled chunks' failures on a
 * `location` navigation, but AFTER them on a history one (Back) — every failure
 * lands in the same few milliseconds either way. Waiting this long lets
 * `beforeunload` arrive before anything is decided; if the page really goes,
 * the wait never ends. A genuine stale build reloads a quarter-second later.
 */
const LEAVE_SETTLE_MS = 250

/** Epoch ms of this page's last `beforeunload`; null when it has not fired or the page came back. */
let leavingSince: number | null = null

/**
 * True while the page is being navigated away from. A chunk that fails now was
 * cancelled by that navigation, not removed by a deploy — reloading would
 * re-open the page being left and cancel the navigation the user asked for.
 */
export function isLeavingPage(): boolean {
  return leavingSince !== null && Date.now() - leavingSince < LEAVING_WINDOW_MS
}

/**
 * Resolves with {@link isLeavingPage} once {@link LEAVE_SETTLE_MS} has passed —
 * the question a chunk failure has to ask before anything reloads.
 */
export function isLeavingPageOnceSettled(): Promise<boolean> {
  return new Promise(resolve => setTimeout(() => resolve(isLeavingPage()), LEAVE_SETTLE_MS))
}

/** sessionStorage key holding the epoch ms of the last automatic stale-build reload. */
const LAST_RELOAD_KEY = 'sf:staleBuildReloadAt'

/**
 * How recently an auto-reload must have happened for us to refuse a second one.
 *
 * Short enough that a genuine second deploy later in the same session still
 * recovers by itself; long enough that a reload which lands on the SAME broken
 * build cannot spin. Only the loop case reaches the manual banner.
 */
const RELOAD_LOOP_WINDOW_MS = 30_000

/** Idempotence within one page load — StrictMode double-invokes effects in dev. */
let recoveryAttempted = false

/**
 * The sessionStorage-backed loop brake plus the actual `location.reload()`.
 * Factored out of {@link recoverFromStaleBuild} so the deferred path (fired
 * later, from an idle notification, well after `recoveryAttempted` is already
 * true) can go through the exact same loop-brake logic instead of duplicating
 * it or bypassing it.
 */
function attemptReload(): 'reloading' | 'manual' {
  let lastReloadAt: number | null = null
  try {
    const raw = window.sessionStorage.getItem(LAST_RELOAD_KEY)
    lastReloadAt = raw !== null ? Number(raw) : null
    if (lastReloadAt !== null && !Number.isFinite(lastReloadAt)) lastReloadAt = null
  } catch {
    return 'manual'
  }

  if (lastReloadAt !== null && Date.now() - lastReloadAt < RELOAD_LOOP_WINDOW_MS) {
    return 'manual'
  }

  try {
    window.sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()))
  } catch {
    return 'manual'
  }

  window.location.reload()
  return 'reloading'
}

/** Unsubscribe for the pending idle-notification, while a reload is deferred. Null when none is pending. */
let _pendingIdleUnsubscribe: (() => void) | null = null

/**
 * IN-163: wait for the registered {@link ReloadBlocker} to go idle, then try the
 * reload again. Re-checks `isBusy()` at fire time — a queue that went busy again
 * between the idle notification being scheduled and it landing must not be
 * reloaded out from under, so that case just waits for the NEXT idle notification
 * instead. Fires the eventual reload through {@link attemptReload}, so the loop
 * brake still applies exactly as it does on the immediate path.
 */
function deferReload(): void {
  if (_pendingIdleUnsubscribe) return // already waiting
  if (!_reloadBlocker) return // isReloadBlocked() was true, so this shouldn't happen

  const blocker = _reloadBlocker
  _pendingIdleUnsubscribe = blocker.subscribeIdle(() => {
    _pendingIdleUnsubscribe?.()
    _pendingIdleUnsubscribe = null

    if (blocker.isBusy()) {
      // Busy again before this notification landed — wait for the next one.
      deferReload()
      return
    }

    if (attemptReload() === 'manual') showStaleBuildBanner()
  })
}

/**
 * Decide how to recover from a stale build, and auto-reload when that is safe.
 *
 * There is no real choice to offer the user here: a chunk the app needs is gone
 * from the server, so the page they are looking at cannot continue either way.
 * The old banner asked them to press a button and then reloaded after 5s
 * regardless — pure friction in front of a foregone conclusion. So the normal
 * path now just reloads, immediately and silently.
 *
 * The one case that must NOT auto-reload immediately is a registered
 * {@link ReloadBlocker} reporting it is busy (IN-163) — reloading mid a
 * multi-hour photo upload destroys the queue, which does not survive a reload.
 * That case defers: it waits for the blocker to go idle and reloads then,
 * automatically, with no button offered in the meantime (a non-technical user
 * must not be given a control that would destroy their own upload).
 *
 * Otherwise, the one case that must NOT auto-reload is a reload that did not fix
 * anything: without a brake, a build that is broken for some other reason would
 * refresh forever. Hence the sessionStorage timestamp — a second failure inside
 * {@link RELOAD_LOOP_WINDOW_MS} falls back to a manual banner instead.
 *
 * Storage can throw (Safari private mode), and an unreadable marker means we
 * cannot prove we are not looping — so that degrades to 'manual' too, which is
 * annoying but never spins.
 *
 * A failure while the page is being left ({@link isLeavingPage}) is not a stale
 * build at all, so it does nothing — and does not use up this page's one
 * recovery attempt, in case the navigation never happens.
 *
 * @returns 'reloading' when a reload has just been triggered, 'deferred' when a
 *   registered blocker is busy and the reload will happen automatically once it
 *   idles, 'manual' when the caller should surface a manual reload affordance,
 *   'leaving' when the page is being navigated away from and nothing should happen.
 */
export function recoverFromStaleBuild(): 'reloading' | 'manual' | 'deferred' | 'leaving' {
  if (isLeavingPage()) return 'leaving'
  if (recoveryAttempted) return 'manual'
  recoveryAttempted = true

  if (isReloadBlocked()) {
    deferReload()
    return 'deferred'
  }

  return attemptReload()
}

function showStaleBuildBanner(): void {
  if (document.getElementById('stale-build-banner')) return

  const banner = document.createElement('div')
  banner.id = 'stale-build-banner'
  // Intentionally inline — this runs before the stylesheet is guaranteed loaded.
  //
  // The insets are not decoration: index.html sets viewport-fit=cover, so on a
  // notched phone in standalone PWA mode a bare top:0 puts this banner UNDER the
  // status bar and the Dynamic Island, which ate the message and clipped the
  // button clean off the right edge. max() keeps the desktop padding when the
  // inset is 0. Wrapping + centred text is what makes it survive 390px.
  banner.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'right:0',
    'z-index:9999',
    'background:#0f172a',
    'color:#f8fafc',
    'padding-top:max(12px,env(safe-area-inset-top,0px))',
    'padding-bottom:12px',
    'padding-left:max(20px,env(safe-area-inset-left,0px))',
    'padding-right:max(20px,env(safe-area-inset-right,0px))',
    'display:flex',
    'flex-wrap:wrap',
    'align-items:center',
    'justify-content:center',
    'text-align:center',
    'gap:8px 16px',
    'font-family:system-ui,sans-serif',
    'font-size:14px',
    'font-weight:500',
    'box-shadow:0 4px 12px rgba(0,0,0,.35)',
  ].join(';')

  const message = document.createElement('span')
  message.textContent = t('errors.staleBuild.message', 'A new version is available.')

  const reloadBtn = document.createElement('button')
  reloadBtn.textContent = t('errors.staleBuild.reload', 'Reload')
  reloadBtn.style.cssText = [
    'background:#3b82f6',
    'color:#fff',
    'border:none',
    'padding:8px 20px',
    'border-radius:6px',
    'cursor:pointer',
    'font-size:13px',
    'font-weight:600',
    'flex-shrink:0',
    // Comfortably tappable one-handed; the old 6px/13px button was below the
    // 44px iOS target even before the Island covered it.
    'min-height:40px',
  ].join(';')
  reloadBtn.onclick = () => window.location.reload()

  banner.append(message, reloadBtn)
  document.body.prepend(banner)
}

/**
 * IN-163: the deferred-reload banner — shown while a registered blocker is busy.
 * Deliberately has NO reload button: pressing one would destroy the very work
 * (an in-progress upload) this whole path exists to protect. The page reloads
 * on its own, automatically, the moment the blocker goes idle (see
 * {@link deferReload}).
 */
function showDeferredBuildBanner(): void {
  if (document.getElementById('stale-build-banner')) return

  const banner = document.createElement('div')
  banner.id = 'stale-build-banner'
  // Same inline-style rationale as showStaleBuildBanner — see its comment.
  banner.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'right:0',
    'z-index:9999',
    'background:#0f172a',
    'color:#f8fafc',
    'padding-top:max(12px,env(safe-area-inset-top,0px))',
    'padding-bottom:12px',
    'padding-left:max(20px,env(safe-area-inset-left,0px))',
    'padding-right:max(20px,env(safe-area-inset-right,0px))',
    'display:flex',
    'flex-wrap:wrap',
    'align-items:center',
    'justify-content:center',
    'text-align:center',
    'gap:8px 16px',
    'font-family:system-ui,sans-serif',
    'font-size:14px',
    'font-weight:500',
    'box-shadow:0 4px 12px rgba(0,0,0,.35)',
  ].join(';')

  const message = document.createElement('span')
  message.textContent = t(
    'errors.staleBuild.deferredMessage',
    'A new version is available. It will load when your uploads finish.',
  )

  banner.append(message)
  document.body.prepend(banner)
}

/**
 * Call once from main.tsx. Sets up the window-level 'vite:preloadError' listener
 * so chunk-load failures are caught even before any React component mounts.
 *
 * IN-189 — `beforeunload` and the settle. A navigation away cancels the page's
 * in-flight chunk loads, and WebKit reports every one of them to the page that
 * is being left, as one 'vite:preloadError' ("Importing a module script
 * failed") per chunk, before `pagehide`. Read as a stale build, that reloaded
 * the OLD page, and the reload cancelled the navigation: on Safari, leaving a
 * page (or pressing Back) while its translations were still loading put the
 * user back where they started. Playwright's mobile-safari project caught it
 * three ways — "navigation to /my-projects is interrupted by another navigation
 * to /login", and two Back tests that ended up on the page they had left. So a
 * failure is judged only after {@link LEAVE_SETTLE_MS}, and not at all if
 * `beforeunload` fired meanwhile. `beforeunload`, not `pagehide`: pagehide comes
 * after the failures. A `beforeunload` listener does not cost the back/forward
 * cache (only `unload` does), and `pageshow` clears the flag when the page comes
 * back from it.
 */
export function initStaleBuildDetector(): void {
  window.addEventListener('beforeunload', () => { leavingSince = Date.now() })
  window.addEventListener('pageshow', (event) => { if (event.persisted) leavingSince = null })
  window.addEventListener('vite:preloadError', (event) => {
    // Prevent Vite's default behaviour (re-throwing as an unhandled error),
    // since we're handling the recovery ourselves.
    event.preventDefault()
    void isLeavingPageOnceSettled().then(() => {
      const outcome = recoverFromStaleBuild()
      if (outcome === 'manual') showStaleBuildBanner()
      else if (outcome === 'deferred') showDeferredBuildBanner()
    })
  })
}
